import { useCallback, useLayoutEffect, useRef, useState, UIEvent } from 'react';

import { ChakraBox, ChakraFlex } from '@equip.health/ui';

import MaiChatAiMessage from './MaiChatAiMessage';
import MaiChatEmptyState from './MaiChatEmptyState';
import MaiChatErrorNotice from './MaiChatErrorNotice';
import MaiChatHistoryLoading from './MaiChatHistoryLoading';
import MaiChatHistoryNotice from './MaiChatHistoryNotice';
import MaiChatJumpToLatest from './MaiChatJumpToLatest';
import MaiChatTypingIndicator from './MaiChatTypingIndicator';
import MaiChatUserMessage from './MaiChatUserMessage';
import { MaiChatErrorKind, MaiMessage } from './maiChat.types';
import { MaiFeedbackRating } from '~/lib/types/mai/MaiFeedbackRating';

// "Near bottom" tolerance in pixels — if the user is within this distance of
// the bottom when a new message arrives we auto-scroll; otherwise we leave
// their scroll position alone so they don't lose context while reading.
const NEAR_BOTTOM_TOLERANCE_PX = 100;

interface MaiChatStreamProps {
  messages: MaiMessage[];
  // True only between send and the first delta, while the AI bubble is deferred.
  isAwaitingResponse: boolean;
  errorKind: MaiChatErrorKind | null;
  isHistoryLoading: boolean;
  isHistoryError: boolean;
  turnKey: number;
  patientFirstName: string;
  clinicianName: string;
  pendingRatingIds: Set<string>;
  onRate: (serverMessageId: string, rating: MaiFeedbackRating) => void;
  onRegenerate: (messageId: string) => void;
  onRetry: (kind: MaiChatErrorKind) => void;
  onRetryHistory: () => void;
  onToolStepsToggle: (
    isExpanded: boolean,
    toolCount: number,
    serverMessageId?: string,
  ) => void;
}

const MaiChatStream = ({
  messages,
  isAwaitingResponse,
  errorKind,
  isHistoryLoading,
  isHistoryError,
  turnKey,
  patientFirstName,
  clinicianName,
  pendingRatingIds,
  onRate,
  onRegenerate,
  onRetry,
  onRetryHistory,
  onToolStepsToggle,
}: MaiChatStreamProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Track whether we should auto-scroll on the next message. Defaults to true
  // (clinician hasn't moved yet, so they're effectively pinned to the bottom).
  // Ref, not state: the auto-scroll effect reads this synchronously inside the
  // same tick as onScroll's write, so a state-batched update would race — a new
  // message arriving in the same tick as scroll-up could re-run the effect
  // against the stale `true` and yank the user back to the bottom. Nothing
  // on-screen depends on this value, so useRef is the right tool.
  const stickToBottomRef = useRef(true);
  // Last measured content height, so the pill only reacts to real growth and not
  // to re-renders that change a message in place (a rating, say).
  const lastScrollHeightRef = useRef(0);
  // Drives the jump-to-latest pill, so this one has to be state.
  const [hasUnseenContent, setHasUnseenContent] = useState(false);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    stickToBottomRef.current = true;
    setHasUnseenContent(false);
  }, []);

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isNearBottom = distanceFromBottom < NEAR_BOTTOM_TOLERANCE_PX;
    stickToBottomRef.current = isNearBottom;
    // Scrolling back down on their own is the same as dismissing the pill.
    if (isNearBottom) setHasUnseenContent(false);
  };

  useLayoutEffect(() => {
    stickToBottomRef.current = true;
    setHasUnseenContent(false);
  }, [turnKey]);

  // useLayoutEffect so the scroll happens before the browser paints — avoids
  // a flash where the new message renders mid-stream and then jumps down.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const previousScrollHeight = lastScrollHeightRef.current;
    lastScrollHeightRef.current = el.scrollHeight;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      return;
    }
    // Content grew below the viewport while they were reading further up.
    if (el.scrollHeight > previousScrollHeight) setHasUnseenContent(true);
  }, [messages, isAwaitingResponse, errorKind, isHistoryLoading, turnKey]);

  const isEmpty =
    messages.length === 0 &&
    !isAwaitingResponse &&
    errorKind === null &&
    !isHistoryLoading &&
    !isHistoryError;

  return (
    <ChakraBox position="relative" flex="1" minHeight={0}>
      {hasUnseenContent && <MaiChatJumpToLatest onClick={scrollToBottom} />}
      <ChakraFlex
        ref={scrollRef}
        data-testid="mai-chat-panel__stream"
        height="100%"
        direction="column"
        gap="16px"
        padding="18px 18px 40px"
        overflowY="auto"
        role="log"
        aria-live="polite"
        aria-busy={isAwaitingResponse}
        onScroll={handleScroll}
      >
        {isHistoryLoading && <MaiChatHistoryLoading />}
        {isHistoryError && <MaiChatHistoryNotice onRetry={onRetryHistory} />}
        {isEmpty && <MaiChatEmptyState patientFirstName={patientFirstName} />}
        {messages.map((message) =>
          message.role === 'user' ? (
            <MaiChatUserMessage
              key={message.id}
              text={message.text ?? ''}
              createdAt={message.createdAt}
              clinicianName={clinicianName}
            />
          ) : (
            <MaiChatAiMessage
              key={message.id}
              messageId={message.id}
              segments={message.segments ?? []}
              status={message.status}
              createdAt={message.createdAt}
              citations={message.citations}
              serverMessageId={message.messageId}
              rating={message.rating}
              isRatingSubmitting={
                message.messageId !== undefined &&
                pendingRatingIds.has(message.messageId)
              }
              onRate={onRate}
              onRegenerate={onRegenerate}
              onToolStepsToggle={onToolStepsToggle}
            />
          ),
        )}
        {isAwaitingResponse && <MaiChatTypingIndicator />}
        {errorKind !== null && (
          <MaiChatErrorNotice kind={errorKind} onRetry={onRetry} />
        )}
      </ChakraFlex>
    </ChakraBox>
  );
};

export default MaiChatStream;
